import { Component } from '@angular/core';
import { CoreProvider } from './services/core.provider';
import { EventType } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent {
  public showMenu$ = new BehaviorSubject<boolean>(false);
  /**
   * True once the viewport is wide enough to show the permanent sidebar.
   * Driven by our own matchMedia listener instead of ion-split-pane's
   * `when` breakpoint, so the menu layout no longer depends on split-pane.
   */
  public readonly isWide$ = new BehaviorSubject<boolean>(false);
  private readonly wideViewportQuery = window.matchMedia('(min-width: 1200px)');
  /** Open/closed state of the custom overlay drawer (narrow viewports). */
  public readonly menuOpen$ = new BehaviorSubject<boolean>(false);
  /**
   * Titles are marked with `_()` for the extractor but left untranslated here —
   * the template pipes them through `translate`, so they re-render on a language
   * change without this array being rebuilt.
   */
  private readonly basePages = [
    // { title: _('Tutorial'), url: '/optin/first-teams', icon: 'heart' },
    { title: _('New Game'), url: '/game-tracker/start-game', icon: 'paper-plane' },
    { title: _('Start Bench'), url: '/game-tracker/connection/bench', icon: 'wifi' },
    { title: _('Past Games'), url: '/game-tracker/past-games', icon: 'file-tray-stacked' },
    { title: _('Scouting'), url: '/game-tracker/scouting', icon: 'videocam' },
    {
      title: _('Manage Teams'),
      url: '/configuration/manage-teams',
      icon: 'people',
    },
    {
      title: _('Elite Config'),
      url: '/configuration/elite-configuration',
      icon: 'apps',
    },
    {
      title: _('Integrations'),
      url: '/configuration/integrations',
      icon: 'link',
    },
    {
      title: _('User profile'),
      url: '/configuration/user-profile',
      icon: 'person',
    },
    // { title: _('Scouting'), url: '/folder/archived', icon: 'trash' },
  ];

  private readonly superadminPage = { title: _('Superadmin'), url: '/superadmin/users', icon: 'shield' };

  /**
   * Menu entries, computed from the current auth state so the Superadmin link appears/disappears
   * immediately on login/logout (no page reload needed). The template tracks by `url`, so this
   * returning a fresh array per change-detection cycle does not churn the DOM.
   */
  public get appPages() {
    return this.core.auth.isSuperadmin ? [...this.basePages, this.superadminPage] : this.basePages;
  }

  private readonly menuDisabledStartUrls = [
    '/login',
    '/optin',
    '/payment-confirmation',
    '/game-tracker/track',
    '/game-tracker/connection',
    '/game-tracker/view-events',
  ];

  constructor(
    public readonly core: CoreProvider,
  ) {
    this.isWide$.next(this.wideViewportQuery.matches);
    this.wideViewportQuery.addEventListener('change', (event) => {
      this.isWide$.next(event.matches);
      // Close the overlay drawer when crossing into the permanent layout.
      if (event.matches) {
        this.closeMenu();
      }
    });

    this.core.router.events.subscribe((routerEvent) => {
      if (routerEvent.type == EventType.NavigationEnd) {
        console.debug('Router Event:', routerEvent);

        const isDisableMenuPage = this.menuDisabledStartUrls.some((urlStart) =>
          routerEvent.urlAfterRedirects.startsWith(urlStart)
        );
        const shouldShowMenu = !isDisableMenuPage;
        this.showMenu$.next(shouldShowMenu);
        // Always collapse the drawer on navigation.
        this.closeMenu();
      }
    });

  }

  /** Toggle the overlay drawer (invoked by the floating handle). */
  public openSideMenu(): void {
    if (!this.showMenu$.value || this.isWide$.value) {
      return;
    }
    this.menuOpen$.next(!this.menuOpen$.value);
  }

  public closeMenu(): void {
    if (this.menuOpen$.value) {
      this.menuOpen$.next(false);
    }
  }

  public onMenuItemClick(): void {
    this.closeMenu();
  }

  /** Applies the pending app update (reload on web, store listing on native). */
  public onUpdateClick(): void {
    this.closeMenu();
    void this.core.versionService.update();
  }

  /** Ends impersonation and returns the superadmin to their own account. */
  public async stopImpersonating(): Promise<void> {
    // Restoring the snapshot rewrites the whole local store; block the UI while it runs. On
    // success the app hard-reloads, so the overlay is only ever dismissed on failure.
    const loading = await this.core.genericLoading(this.core.trans.instant(_('Restoring your account…')));
    try {
      await this.core.auth.stopImpersonating();
    } catch (err) {
      console.error('Error restoring superadmin session', err);
      await this.core.errorToast(loading, this.core.trans.instant(_('Error restoring your account')));
    }
  }

}
